import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  Field,
  IconFolder,
  IconPlus,
  Input,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from "@dream/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ApiError, api, unwrap } from "../api/client.ts";
import { useAdminCatalog } from "../hooks/use-calendar.ts";
import { calendarSearch } from "../router.tsx";

interface ProjectForm {
  clientId: string;
  name: string;
  description: string;
  color: string;
}

const STATUS_LABELS: Record<string, string> = {
  active: "Ativo",
  paused: "Pausado",
  completed: "Concluído",
  archived: "Arquivado",
};

export function ProjectsPage() {
  const catalog = useAdminCatalog();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);

  const form = useForm<ProjectForm>({
    defaultValues: {
      clientId: "",
      name: "",
      description: "",
      color: "#E6DBF4",
    },
  });

  const createMutation = useMutation({
    mutationFn: async (values: ProjectForm) =>
      unwrap(
        await api.api.admin.projects.post({
          clientId: values.clientId,
          name: values.name.trim(),
          description: values.description.trim() === "" ? null : values.description.trim(),
          color: values.color,
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["catalog"] });
      toast.success("Projeto criado");
      form.reset();
      setCreating(false);
    },
    onError: (error: unknown) =>
      toast.error(
        "Não foi possível criar o projeto",
        error instanceof ApiError ? error.message : "Tente novamente.",
      ),
  });

  const clientName = (clientId: string) =>
    catalog.clients.find((c) => c.id === clientId)?.name ?? "Cliente";

  const canCreate = catalog.clients.length > 0;

  return (
    <div className="dream-scroll min-h-0 flex-1 overflow-auto p-4 md:p-6">
      <div className="mx-auto max-w-5xl">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-[1.25rem] font-bold leading-[1.35] tracking-[0] text-[var(--color-ink)] sm:text-[1.5rem]">
              Projetos e campanhas
            </h1>
            <p className="mt-0.5 max-w-xl text-[0.8125rem] leading-5 text-[var(--color-ink-muted)]">
              Vivem dentro de um cliente e agrupam as entregas no calendário — um editorial mensal,
              uma campanha, um lançamento.
            </p>
          </div>
          <Button
            variant="primary"
            className="w-full shrink-0 sm:w-auto"
            disabled={!canCreate}
            title={canCreate ? undefined : "Cadastre um cliente primeiro"}
            onClick={() => {
              form.setValue("clientId", catalog.clients[0]?.id ?? "");
              setCreating(true);
            }}
          >
            <IconPlus className="size-4" />
            Novo projeto
          </Button>
        </div>

        {catalog.isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        ) : catalog.projects.length === 0 ? (
          <Card>
            <EmptyState
              icon={<IconFolder className="size-5" />}
              title="Nenhum projeto ou campanha"
              description={
                canCreate
                  ? "Crie um projeto ou uma campanha para organizar as entregas do cliente no calendário."
                  : "Cadastre um cliente antes de criar projetos e campanhas."
              }
              action={
                canCreate ? (
                  <Button
                    variant="primary"
                    onClick={() => {
                      form.setValue("clientId", catalog.clients[0]?.id ?? "");
                      setCreating(true);
                    }}
                  >
                    <IconPlus className="size-4" />
                    Criar projeto
                  </Button>
                ) : (
                  <Link
                    to="/clientes"
                    className="text-[0.875rem] font-medium text-brand-700 hover:underline"
                  >
                    Ir para clientes
                  </Link>
                )
              }
            />
          </Card>
        ) : (
          <Card className="divide-y divide-[var(--color-line)] overflow-hidden">
            {catalog.projects.map((project) => (
              <div
                key={project.id}
                className="flex items-center gap-3 px-4 py-3 transition-colors duration-[var(--dream-duration-fast)] hover:bg-[var(--color-surface-muted)]"
              >
                <span
                  aria-hidden="true"
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: project.color }}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[0.875rem] font-medium text-[var(--color-ink)]">
                    {project.name}
                  </p>
                  <p className="truncate text-[0.75rem] text-[var(--color-ink-muted)]">
                    {clientName(project.clientId)}
                    {project.description !== null && project.description !== ""
                      ? ` · ${project.description}`
                      : ""}
                  </p>
                </div>
                <Badge tone={project.status === "active" ? "success" : "neutral"}>
                  {STATUS_LABELS[project.status] ?? project.status}
                </Badge>
                <Link
                  to="/calendario"
                  search={calendarSearch({ projects: project.id })}
                  className="shrink-0 text-[0.8125rem] font-medium text-brand-700 hover:underline"
                >
                  Ver entregas
                </Link>
              </div>
            ))}
          </Card>
        )}
      </div>

      <Dialog
        open={creating}
        onOpenChange={setCreating}
        title="Novo projeto ou campanha"
        description="As entregas deste projeto ou campanha aparecem no calendário do cliente."
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreating(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              loading={createMutation.isPending}
              onClick={() => void form.handleSubmit((v) => createMutation.mutate(v))()}
            >
              Criar projeto
            </Button>
          </>
        }
      >
        <form
          onSubmit={form.handleSubmit((v) => createMutation.mutate(v))}
          className="space-y-4"
          noValidate
        >
          <Field label="Cliente" htmlFor="project-client" required>
            <Select id="project-client" {...form.register("clientId", { required: true })}>
              {catalog.clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Nome do projeto"
            htmlFor="project-name"
            required
            error={form.formState.errors.name?.message}
          >
            <Input
              id="project-name"
              autoFocus
              placeholder="Ex.: Social Media — Setembro, ou Campanha Black Friday"
              invalid={form.formState.errors.name !== undefined}
              {...form.register("name", {
                required: "Informe o nome",
                minLength: { value: 2, message: "Mínimo de 2 caracteres" },
              })}
            />
          </Field>

          <Field label="Cor" htmlFor="project-color">
            <div className="flex items-center gap-2">
              <input
                id="project-color"
                type="color"
                className="h-9 w-14 cursor-pointer rounded-[var(--radius-md)] border border-[var(--color-line-strong)] bg-[var(--color-surface)] p-1"
                {...form.register("color")}
              />
              <Input className="flex-1 font-mono text-[0.8125rem]" {...form.register("color")} />
            </div>
          </Field>

          <Field label="Descrição" htmlFor="project-description">
            <Textarea id="project-description" rows={3} {...form.register("description")} />
          </Field>
        </form>
      </Dialog>
    </div>
  );
}
